import { collection, doc, setDoc, deleteDoc, getDocs, query, where, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { normalizeForComparison } from './requests';

// Deterministic group key for fuzzy-grouped songs
export function makePPGroupKey(title: string, artist: string): string {
  return `${normalizeForComparison(title)}|${normalizeForComparison(artist)}`;
}

// Firestore collection name
const COLL = 'nominationUpvotes';

// Doc ID format: {eventId}_{groupKey}_{attendeeId}
function upvoteDocId(eventId: string, groupKey: string, attendeeId: string): string {
  return `${eventId}_${groupKey}_${attendeeId}`;
}

export async function upvoteNomination(
  eventId: string,
  groupKey: string,
  attendeeId: string,
  primaryRequestId: string
): Promise<void> {
  const docId = upvoteDocId(eventId, groupKey, attendeeId);
  await setDoc(doc(db, COLL, docId), {
    eventId,
    groupKey,
    attendeeId,
    primaryRequestId,
    createdAt: serverTimestamp(),
  });
}

export async function removeNominationUpvote(
  eventId: string,
  groupKey: string,
  attendeeId: string
): Promise<void> {
  const docId = upvoteDocId(eventId, groupKey, attendeeId);
  await deleteDoc(doc(db, COLL, docId));
}

// Returns count of upvotes and list of upvoter attendeeIds for a given group
export async function getUpvotesForGroup(
  eventId: string,
  groupKey: string
): Promise<{ count: number; attendeeIds: string[] }> {
  const snap = await getDocs(query(
    collection(db, COLL),
    where('eventId', '==', eventId),
    where('groupKey', '==', groupKey)
  ));
  const attendeeIds = snap.docs.map(d => d.data().attendeeId as string);
  return { count: attendeeIds.length, attendeeIds };
}

// Returns set of groupKeys this attendee has upvoted in this event
export async function getUpvotedGroupKeysByAttendee(
  eventId: string,
  attendeeId: string
): Promise<Set<string>> {
  const snap = await getDocs(query(
    collection(db, COLL),
    where('eventId', '==', eventId),
    where('attendeeId', '==', attendeeId)
  ));
  return new Set(snap.docs.map(d => d.data().groupKey as string));
}

// Returns map of groupKey → upvote count for an entire event (for bulk display)
export async function getUpvoteCountsForEvent(
  eventId: string
): Promise<Map<string, number>> {
  const snap = await getDocs(query(
    collection(db, COLL),
    where('eventId', '==', eventId)
  ));
  const counts = new Map<string, number>();
  for (const d of snap.docs) {
    const key = d.data().groupKey as string;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export function subscribeToUpvoteCountsForEvent(
  eventId: string,
  callback: (counts: Map<string, number>) => void
): () => void {
  const q = query(
    collection(db, COLL),
    where('eventId', '==', eventId)
  );
  return onSnapshot(q, (snap) => {
    const counts = new Map<string, number>();
    for (const d of snap.docs) {
      const key = d.data().groupKey as string;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    callback(counts);
  });
}

// Delete all upvotes for a given group (called when nomination graduates or is rejected)
export async function deleteUpvotesForGroup(
  eventId: string,
  groupKey: string
): Promise<void> {
  const snap = await getDocs(query(
    collection(db, COLL),
    where('eventId', '==', eventId),
    where('groupKey', '==', groupKey)
  ));
  for (const d of snap.docs) {
    await deleteDoc(d.ref);
  }
}

// ── localStorage dismissed helpers ──────────────────────────────────────────
const dismissedKey = (eventId: string) => `votebeats_dismissed_pp_${eventId}`;

export function getDismissedGroupKeys(eventId: string): Set<string> {
  try {
    const raw = localStorage.getItem(dismissedKey(eventId));
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

export function dismissGroupKey(eventId: string, groupKey: string): void {
  const existing = getDismissedGroupKeys(eventId);
  existing.add(groupKey);
  localStorage.setItem(dismissedKey(eventId), JSON.stringify([...existing]));
}
